

































import { computed, defineComponent, onMounted, ref, watch } from '@nuxtjs/composition-api';
import useVehicleManualSearch from '@shared/composables/useVehicleManualSearch';
import {
  clearFieldSelection,
  toVehicleSelection,
  updateFormOptions,
  updateSelectedOptions,
  validate,
} from '@shared/helpers/vehiclesManualSearch';
import {
  IMotorcycleValidation,
  ISelectedOption,
  IVehicleSelection,
  IVehicleSelectionOption,
} from '@shared/types/IVehicleSelection';
import { UCButtonGroup, UCIcon } from '@shared/ui';

import AMXButton from '~/components/AMXButton.vue';
import AMXFormField from '~/components/AMXFormField.vue';
import Selection from '~/components/Selection/Selection.vue';
import ValidationErrorMessage from '~/components/ValidationErrorMessage.vue';
import { FORM_FIELD_CONFIG } from '~/constants';

export default defineComponent({
  name: 'VehicleManualSearchForm',
  components: {
    ValidationErrorMessage,
    AMXFormField,
    Selection,
    UCButtonGroup,
    AMXButton,
    UCIcon,
  },
  props: {
    id: {
      type: String,
      default: '',
    },
    inline: {
      type: Boolean,
      default: false,
    },
  },
  // eslint-disable-next-line max-lines-per-function
  setup(_, { emit }) {
    const { search, loading, results, getMakes, getModels, getYears } = useVehicleManualSearch();
    const showErrors = ref(false);
    const searchError = ref('');
    const validation = ref<IMotorcycleValidation>({
      make: null,
      model: null,
      year: null,
    });
    const selectedOptions = ref<ISelectedOption[]>([]);
    const formConfig = ref<IVehicleSelection[]>([]);
    const isUsingSubmodel = ref(true);

    const vehicleSelection = computed(() =>
      toVehicleSelection(selectedOptions.value, isUsingSubmodel.value),
    );

    const initField = (fieldTitle: string, options: IVehicleSelectionOption[]) => {
      clearFieldSelection(selectedOptions, fieldTitle);
      updateFormOptions(formConfig, fieldTitle, options);
    };

    const onOptionSelected = async (selectedOption: ISelectedOption) => {
      updateSelectedOptions(selectedOptions, selectedOption);
      if (selectedOption.title === 'Make') {
        const { submodels, models } = await getModels(vehicleSelection.value);
        isUsingSubmodel.value = !!submodels?.length;
        initField('Model', isUsingSubmodel.value ? submodels : models);
        initField('Year', []);
      }
      if (selectedOption.title === 'Model') {
        initField('Year', await getYears(vehicleSelection.value));
      }
    };

    const getValidationError = (fieldTitle: string) => {
      if (!showErrors.value) return;
      return validation.value[fieldTitle.toLowerCase() as keyof IMotorcycleValidation];
    };

    const onSearchSubmit = async () => {
      showErrors.value = true;
      if (!validate(validation, selectedOptions)) return;
      searchError.value = '';
      await search(vehicleSelection.value);
      if (results.value?.length) {
        emit('on-search-response');
      } else {
        searchError.value = 'Bike not found! Please review your selection.';
      }
    };

    const FIELD_ORDER = ['Make', 'Model', 'Year'];

    const clearSelections = (fieldTitle: string) => {
      const startIdx = FIELD_ORDER.indexOf(fieldTitle);
      if (startIdx === -1) return;

      clearFieldSelection(selectedOptions, fieldTitle);
      FIELD_ORDER.slice(startIdx + 1).forEach((f) => initField(f, []));
    };

    onMounted(async () => {
      formConfig.value = FORM_FIELD_CONFIG.map((config) => ({ ...config, options: [] }));
      initField('Make', await getMakes());
    });

    watch(selectedOptions, () => {
      searchError.value = '';
      const isValid = validate(validation, selectedOptions);
      if (isValid) showErrors.value = false;
    });

    return {
      formConfig,
      selectedOptions,
      searchError,
      loading,
      onOptionSelected,
      onSearchSubmit,
      getValidationError,
      clearSelections,
    };
  },
});
